<script lang="ts">
export interface SelectBuilderProps<T extends AcceptableValue, M extends boolean>
	extends SelectProps<T, M>,
		SelectItemProps<T>,
		SelectGroupProps<T> {}
</script>

<script setup lang="ts" generic="T extends AcceptableValue, M extends boolean">
import { useForwardPropsEmits, type AcceptableValue } from "reka-ui";
import { computed } from "vue";
import { useItems, type SelectItem, type SelectItemProps } from "./composables/select-items";
import { useItemsGroups, type SelectGroupProps } from "./composables/select-items-group";
import Select, { type InferValue, type SelectEmit, type SelectProps } from "./Select.vue";
import SelectGroup from "./SelectGroup.vue";
import SelectOption from "./SelectOption.vue";

defineOptions({ inheritAttrs: false });

const props = defineProps<
	Omit<SelectBuilderProps<T, M>, "multiple"> & { multiple?: M & boolean }
>();
const emit = defineEmits<SelectEmit<T, M>>();

const forwarded = useForwardPropsEmits(props, emit);

defineSlots<{
	prepend(): void;
	append(): void;
	selection(slotProps: { modelValue: InferValue<T, M> }): void;
	item(args: { item: SelectItem<T> }): void;
}>();

const { makeItems } = useItems(props);
const groupItems = useItemsGroups<T>(props);

const items = computed(() => groupItems(makeItems(props.items)));
</script>

<template>
	<Select :class="$attrs.class" v-bind="forwarded">
		<template #selection="slotProps">
			<slot name="selection" v-bind="slotProps" />
		</template>
		<template #prepend>
			<slot name="prepend" />
		</template>

		<template #append>
			<slot name="append" />
		</template>

		<SelectGroup v-for="(group, idx) of items" :key="idx" :label="group.label">
			<SelectOption
				v-for="item of group.items"
				:key="item.value"
				:value="item.value"
				:disabled="item.disabled"
			>
				<slot name="item" :item="item">{{ item.text }}</slot>
			</SelectOption>
		</SelectGroup>
	</Select>
</template>
